import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider'; // Assuming AuthProvider has changePassword
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { getUserById, updateUser, searchUserByPhone } from '../services/userApi';
import { Eye, EyeOff } from 'lucide-react';
import countryData from '../data/country.json';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

const ProfilePage = () => {
  const { user, updateUserAttributes, checkAuthStatus } = useAuth();
  const navigate = useNavigate();
  const location = useLocation(); // Keep location for 'from' state
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    fullName: '',
    gender: '',
    phone: '',
    preferredLanguage: i18n.language,
    country: '',
    address: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [nationSearch, setNationSearch] = useState('');
  const [isNationDropdownOpen, setIsNationDropdownOpen] = useState(false);
  const nationDropdownRef = useRef(null);
  const [isPhoneChecking, setIsPhoneChecking] = useState(false);
  const [isPhoneValid, setIsPhoneValid] = useState(false);

  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

   const languages = [
     { code: 'en', name: t('languages.en', 'English') },
    { code: 'te', name: t('languages.te', 'తెలుగు') },
    { code: 'or', name: t('languages.or', 'ଓଡ଼ିଆ') },
    { code: 'kn', name: t('languages.kn', 'ಕನ್ನಡ') },
    { code: 'ml', name: t('languages.ml', 'മലയാളം') },
    { code: 'bn', name: t('languages.bn', 'বাংলা') },
    { code: 'mr', name: t('languages.mr', 'मराठी') },
    { code: 'gu', name: t('languages.gu', 'ગુજરાતી') },
    { code: 'ta', name: t('languages.ta', 'தமிழ்') },
    { code: 'hi', name: t('languages.hi', 'हिंदी') },
  ];

  const from = location.state?.from?.pathname || '/home';

  const nations = useMemo(() =>
    Object.entries(countryData.data)
      .map(([code, { country }]) => ({ code, name: country }))
      .sort((a, b) => a.name.localeCompare(b.name)),
    []
  );

  const { data: userData, isLoading: isQueryLoading, error: queryError } = useQuery(
    ['user', user?.sub],
    () => getUserById(user.sub),
    {
      enabled: !!user?.sub,
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
    }
  );

  useEffect(() => {
    if (userData) {
      const profileLang = userData.preferredLanguage || i18n.language;
      if (i18n.language !== profileLang) {
        i18n.changeLanguage(profileLang);
      }
      setFormData(prev => ({ ...prev, ...userData, preferredLanguage: profileLang, phone: userData.phone || '' }));
      if (userData.country) {
        const countryName = nations.find(n => n.code === userData.country)?.name || '';
        setNationSearch(countryName);
      } else {
        setNationSearch('');
      }
    }
  }, [userData, i18n, nations]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (nationDropdownRef.current && !nationDropdownRef.current.contains(event.target)) {
        setIsNationDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const { mutateAsync: updateUserMutation, isLoading: isUpdating, error: updateError } = useMutation(
    (data) => updateUser(user.sub, data),
    {
      onSuccess: async () => {
        queryClient.invalidateQueries(['user', user?.sub]);
        await checkAuthStatus();
      },
    }
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (name === 'phone') {
      setIsPhoneValid(false);
      setFormErrors(errors => ({ ...errors, phone: undefined }));
    }
  };

  const handlePhoneChange = (value) => {
    setFormData({ ...formData, phone: value });
    setIsPhoneValid(false);
    setFormErrors(errors => ({ ...errors, phone: undefined }));
  };

  const handlePhoneBlur = async () => {
    const phone = formData.phone;
    setIsPhoneValid(false);
    if (phone && isValidPhoneNumber(phone)) {
      setIsPhoneChecking(true);
      setFormErrors(errors => ({ ...errors, phone: undefined }));
      try {
        const existingUsers = await searchUserByPhone(phone);
        // If the phone number exists and it's NOT the current user's phone number
        if (existingUsers && existingUsers.length > 0 && existingUsers[0].userId !== user.sub) {
          setFormErrors(errors => ({ ...errors, phone: 'This phone number is already registered by another user.' }));
        } else {
          setIsPhoneValid(true);
        }
      } catch (error) {
        console.error('Phone validation failed:', error);
        setFormErrors(errors => ({ ...errors, phone: 'Failed to verify phone number.' }));
      } finally {
        setIsPhoneChecking(false);
      }
    }
  };

  const handleNationSearchChange = (e) => {
    setNationSearch(e.target.value);
    if (!isNationDropdownOpen) {
      setIsNationDropdownOpen(true);
    }
  };

  const handleNationSelect = (nation) => {
    setFormData({ ...formData, country: nation.code });
    setNationSearch(nation.name);
    setIsNationDropdownOpen(false);
  };

  const filteredNations = useMemo(() => {
    const lowerCaseSearch = nationSearch.toLowerCase();
    if (!lowerCaseSearch) {
      return nations;
    }

    return nations
      .filter(nation =>
        nation.name.toLowerCase().includes(lowerCaseSearch)
      )
      .sort((a, b) => {
        const aName = a.name.toLowerCase();
        const bName = b.name.toLowerCase();

        const aIsExact = aName === lowerCaseSearch;
        const bIsExact = bName === lowerCaseSearch;
        if (aIsExact && !bIsExact) return -1;
        if (!aIsExact && bIsExact) return 1;

        const aStartsWith = aName.startsWith(lowerCaseSearch);
        const bStartsWith = bName.startsWith(lowerCaseSearch);
        if (aStartsWith && !bStartsWith) return -1;
        if (!aStartsWith && bStartsWith) return 1;

        return a.name.localeCompare(b.name);
      });
  }, [nations, nationSearch]);

  const isPasswordStrong = (password) => {
    return /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&^()_+\-=[\]{};':"\\|,.<>/?]).{8,}$/.test(password);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormErrors({});
    const errors = {};

    // --- 1. Validation ---
    if (!formData.fullName.trim()) {
      errors.fullName = 'Full name is required.';
    }
    if (!formData.phone || !isValidPhoneNumber(formData.phone)) {
      errors.phone = 'A valid contact number is required.';
    }

    const currentPassword = e.target.currentPassword?.value;
    const newPassword = e.target.newPassword?.value;
    const confirmNewPassword = e.target.confirmNewPassword?.value;

    const isChangingPassword = newPassword || currentPassword || confirmNewPassword;

    if (isChangingPassword) {
      if (!newPassword) {
        errors.newPassword = 'Please enter a new password to change it.';
      } else if (!isPasswordStrong(newPassword)) {
        errors.newPassword = 'Password must be at least 8 characters and include a letter, a number, and a special character.';
      }
      if (newPassword !== confirmNewPassword) {
        errors.confirmNewPassword = 'The new passwords do not match.';
      }
      // For Cognito users, current password is required to change password via SDK.
      if (user && typeof user.changePassword === 'function' && !currentPassword) {
        errors.currentPassword = 'Current password is required to set a new one.';
      }
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    // --- 2. Execution ---
    try {
      // For Cognito users, password change is a separate AWS SDK call. Do it first.
      if (isChangingPassword && user && typeof user.changePassword === 'function') {
        await user.changePassword(currentPassword, newPassword);
      }

      // Prepare data for our backend.
      const { email, ...profileData } = formData; // Destructure to remove email if it exists
      const updatedUserData = { ...profileData };
      if (isChangingPassword) {
        updatedUserData.password = newPassword;
      }

      // Update our backend. This will handle profile fields and password.
      await updateUserMutation(updatedUserData);

      // Also update cognito attributes if needed (e.g., name)
      if (user && typeof user.changePassword === 'function' && formData.fullName !== user.attributes.name) {
        await updateUserAttributes({ name: formData.fullName });
      }

      alert('Profile updated successfully!');
      navigate(from);

    } catch (err) {
      setFormErrors({ submit: err.message || 'An error occurred during profile update.' });
    }
  };

  const isLoading = isUpdating || isQueryLoading;
  const error = updateError || queryError;

  return (
    <div className="app-container" style={{ background: 'linear-gradient(180deg, #0b3a7a 0%, #0b234a 100%)', minHeight: '100vh' }}>
      <div style={{ padding: '2rem' }}>
        <div className="content-card" style={{ maxWidth: '600px', margin: '0 auto' }}>
          <h2 style={{ textAlign: 'center', marginBottom: '2rem', color: '#333' }}>
            User Profile
          </h2>
          <p style={{ textAlign: 'center', marginBottom: '1rem', color: '#333' }}>
            Logged in as: <strong>{userData ? userData.fullName : user?.attributes?.name}</strong>
          </p>

          {isQueryLoading && <p style={{ textAlign: 'center', color: 'white' }}>Loading profile...</p>}

          {!isQueryLoading && (
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '1.5rem' }}>
                <label className="form-label">{t('form.name')}</label>
                <input
                  type="text"
                  name="fullName"
                  className="form-input"
                  placeholder={t('form.name')}
                  value={formData.fullName}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label className="form-label">Email</label>
                <input
                  type="email"
                  className="form-input"
                  value={user?.attributes?.email || ''}
                  disabled
                  placeholder="Email"
                />
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label className="form-label">{t('form.contact')}</label>
                <div style={{ position: 'relative' }}>
                  <PhoneInput
                    placeholder={t('form.contact')}
                    value={formData.phone}
                    onChange={handlePhoneChange}
                    onBlur={handlePhoneBlur}
                    numberInputProps={{ className: 'form-input' }}
                    defaultCountry="IN"
                  />
                  {isPhoneValid && !isPhoneChecking && (
                    <span style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', color: 'green', fontSize: '1.2rem', fontWeight: 'bold' }}>
                      ✓
                    </span>
                  )}
                </div>
                {isPhoneChecking && <div style={{ color: 'blue', marginTop: '5px', fontSize: '0.8rem' }}>Checking phone number...</div>}
                {formErrors.phone && <div style={{ color: 'red', marginTop: '5px', fontSize: '0.8rem' }}>{formErrors.phone}</div>}
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label className="form-label">{t('form.gender')}</label>
                <div style={{ position: 'relative' }}>
                  <select
                    name="gender"
                    className="form-input"
                    value={formData.gender}
                    onChange={handleInputChange}
                    style={{ appearance: 'none', paddingRight: '2rem', background: 'transparent' }}
                  >
                    <option value="">{t('form.selectGender', 'Select Gender')}</option>
                    <option value="male">{t('form.male')}</option>
                    <option value="female">{t('form.female')}</option>
                    <option value="other">{t('form.other')}</option>
                  </select>
                  <div style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>⬇️</div>
                </div>
              </div>

              <div style={{ marginBottom: '1.5rem' }} ref={nationDropdownRef}>
                <label className="form-label">{t('form.nation')}</label>
                <div style={{ position: 'relative' }}>
                  {/* Nation search input and dropdown logic is already present and correct */}
                  <input type="text" name="nationSearch" className="form-input" placeholder={t('form.searchNation', 'Search for a nation...')} value={nationSearch} onChange={handleNationSearchChange} onFocus={() => setIsNationDropdownOpen(true)} autoComplete="off" />
                  {isNationDropdownOpen && (
                    <ul style={{
                      position: 'absolute',
                      width: '100%',
                      background: 'white',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      listStyle: 'none',
                      padding: 0,
                      margin: '5px 0 0 0',
                      maxHeight: '200px',
                      overflowY: 'auto',
                      zIndex: 1000
                    }}>
                      {filteredNations.length > 0 ? (
                        filteredNations.map((nation) => (
                          <li
                            key={nation.code}
                            onClick={() => handleNationSelect(nation)}
                            style={{ padding: '10px', cursor: 'pointer', color: '#333' }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                          >
                            {nation.name}
                          </li>
                        ))
                      ) : (<li style={{ padding: '10px', color: '#888' }}>No nations found</li>)}
                    </ul>
                  )}
                </div>
                {formErrors.country && <div style={{ color: 'red', marginTop: '5px', fontSize: '0.8rem' }}>{formErrors.country}</div>}
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label className="form-label">{t('Address')}</label>
                <input
                  type="text"
                  name="address"
                  className="form-input"
                  placeholder={t('Address')}
                  value={formData.address}
                  onChange={handleInputChange}
                />
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label className="form-label">{t('Preferred Language')}</label>
                <div style={{ position: 'relative' }}>
                  <select
                    name="preferredLanguage"
                    className="form-input"
                    value={formData.preferredLanguage}
                    onChange={handleInputChange}
                    required
                    style={{ appearance: 'none', paddingRight: '2rem', background: 'transparent' }}
                  >
                    <option value="">Select Language</option>
                    {languages.map((lang) => (
                      <option key={lang.code} value={lang.code}>{lang.name}</option>
                    ))}
                  </select>
                  <div style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>⬇️</div>
                </div>
              </div>

              <hr style={{ margin: '2rem 0', borderColor: '#ccc' }} />

              <h3 style={{ color: '#333', marginBottom: '1.5rem' }}>Change Password (Optional)</h3>

              {/* Current Password - only shown for Cognito users who need it */}
              {(user && typeof user.changePassword === 'function') && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <label className="form-label">Current Password</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showPasswords.current ? 'text' : 'password'}
                      name="currentPassword"
                      className="form-input"
                      placeholder="Enter your current password"
                    />
                    <button type="button" onClick={() => setShowPasswords(s => ({ ...s, current: !s.current }))} style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', cursor: 'pointer' }}>
                      {showPasswords.current ? <EyeOff size={20} color="#333" /> : <Eye size={20} color="#333" />}
                    </button>
                  </div>
                  {formErrors.currentPassword && <div style={{ color: 'red', marginTop: '5px', fontSize: '0.8rem' }}>{formErrors.currentPassword}</div>}
                </div>
              )}

              {/* New Password */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label className="form-label">New Password</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPasswords.new ? 'text' : 'password'}
                    name="newPassword"
                    className="form-input"
                    placeholder="Enter new password"
                    pattern="^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&^()_+\-=\[\]{};':&quot;\\|,.<>\/?]).{8,}$"
                    title="Password must be at least 8 characters and include an alphabet, a number, and a special character."
                  />
                  <button type="button" onClick={() => setShowPasswords(s => ({ ...s, new: !s.new }))} style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', cursor: 'pointer' }}>
                    {showPasswords.new ? <EyeOff size={20} color="#333" /> : <Eye size={20} color="#333" />}
                  </button>
                </div>
                {formErrors.newPassword && <div style={{ color: 'red', marginTop: '5px', fontSize: '0.8rem' }}>{formErrors.newPassword}</div>}
              </div>

              {/* Confirm New Password */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label className="form-label">Confirm New Password</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPasswords.confirm ? 'text' : 'password'}
                    name="confirmNewPassword"
                    className="form-input"
                    placeholder="Confirm new password"
                  />
                  <button type="button" onClick={() => setShowPasswords(s => ({ ...s, confirm: !s.confirm }))} style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', cursor: 'pointer' }}>
                    {showPasswords.confirm ? <EyeOff size={20} color="#333" /> : <Eye size={20} color="#333" />}
                  </button>
                </div>
                {formErrors.confirmNewPassword && <div style={{ color: 'red', marginTop: '5px', fontSize: '0.8rem' }}>{formErrors.confirmNewPassword}</div>}
              </div>

              {error && <p style={{ color: 'red' }}>Error: {error.message}</p>}
              {formErrors.submit && <p style={{ color: 'red', textAlign: 'center' }}>{formErrors.submit}</p>}

              <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
                <button type="submit" className="btn-primary" disabled={isLoading} style={{ flex: 1 }}>
                  {isLoading ? t('common.loading') : t('common.submit')}
                </button>

                <button type="button" className="btn-secondary" onClick={() => navigate(-1)} style={{ flex: 1 }} disabled={isUpdating}>
                  {t('common.back')}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;